import React from 'react';
import { Box, Button, Flex, HStack, Text, Badge } from '@chakra-ui/react';
import type { StreamServer } from '../types';
import {
  streamGhostDangerHover,
  streamGhostOnDarkHover,
  streamOutlineOnDarkHover,
} from '../darkThemeButtonProps';

type Status = 'active' | 'saved' | 'error';

interface Props {
  server: StreamServer;
  status: Status;
  loading?: boolean;
  onConnect: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onTest?: () => void;
}

function kindLabel(s: StreamServer): string {
  const k = s.sourceKind ?? 'xtream';
  if (k === 'm3u_url') return 'M3U URL';
  if (k === 'm3u_file') return 'M3U file';
  return 'Xtream';
}

export function ServerCard({
  server,
  status,
  loading,
  onConnect,
  onEdit,
  onDuplicate,
  onDelete,
  onTest,
}: Props) {
  return (
    <Box
      borderRadius="lg"
      borderWidth="1px"
      borderColor={status === 'active' ? 'teal.500' : 'whiteAlpha.100'}
      bg="whiteAlpha.50"
      px={3}
      py={2.5}
    >
      <Flex align="flex-start" justify="space-between" gap={3} flexWrap="wrap">
        <Box minW={0} flex={1}>
          <Text fontWeight="700" color="white" fontSize="sm" noOfLines={1}>
            {server.name}
          </Text>
          <Text fontSize="xs" color="whiteAlpha.500" noOfLines={1} mt={0.5}>
            {server.server}
          </Text>
          {(server.sourceKind ?? 'xtream') !== 'm3u_url' && server.username.trim() !== '' && (
            <Text fontSize="xs" color="whiteAlpha.400" noOfLines={1}>
              {server.username}
            </Text>
          )}
          <HStack mt={1.5} spacing={1.5} flexWrap="wrap">
            <Badge colorScheme="gray" variant="subtle" fontSize="10px">
              {kindLabel(server)}
            </Badge>
            {status === 'active' && (
              <Badge colorScheme="green" variant="solid" fontSize="10px">
                Active
              </Badge>
            )}
            {status === 'saved' && (
              <Badge colorScheme="blue" variant="subtle" fontSize="10px">
                Saved
              </Badge>
            )}
            {status === 'error' && server.lastError && (
              <Badge colorScheme="red" variant="subtle" fontSize="10px" maxW="200px" title={server.lastError}>
                Error
              </Badge>
            )}
          </HStack>
        </Box>
        <HStack spacing={1} flexWrap="wrap" flexShrink={0}>
          <Button size="xs" colorScheme="teal" onClick={onConnect} isLoading={loading}>
            Connect
          </Button>
          {(server.sourceKind ?? 'xtream') === 'xtream' && onTest && (
            <Button
              size="xs"
              variant="outline"
              borderColor="whiteAlpha.300"
              color="white"
              {...streamOutlineOnDarkHover}
              onClick={onTest}
            >
              Test
            </Button>
          )}
          <Button size="xs" variant="ghost" color="whiteAlpha.700" {...streamGhostOnDarkHover} onClick={onEdit}>
            Edit
          </Button>
          <Button size="xs" variant="ghost" color="whiteAlpha.700" {...streamGhostOnDarkHover} onClick={onDuplicate}>
            Duplicate
          </Button>
          <Button size="xs" variant="ghost" color="red.300" {...streamGhostDangerHover} onClick={onDelete}>
            Delete
          </Button>
        </HStack>
      </Flex>
    </Box>
  );
}
